import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore,
  ContentCopy,
  PlayArrow,
  VideoCall,
  Edit,
  Download,
  Visibility,
  Analytics,
  AutoAwesome,
  SmartDisplay
} from '@mui/icons-material';
import { videoAPI, VideoScriptResponse, VideoFromPostResponse } from '../../services/videoAPI';

interface EnhancedVideoScriptsGeneratorProps {
  onScriptGenerated?: (script: VideoScriptResponse) => void;
  initialTopic?: string;
  initialPlatform?: string;
}

export const EnhancedVideoScriptsGenerator: React.FC<EnhancedVideoScriptsGeneratorProps> = ({
  onScriptGenerated,
  initialTopic = '',
  initialPlatform = 'linkedin'
}) => {
  const [tabValue, setTabValue] = useState(0);
  
  // Script Generation State
  const [topic, setTopic] = useState(initialTopic);
  const [platform, setPlatform] = useState(initialPlatform);
  const [duration, setDuration] = useState<'short' | 'medium' | 'long'>('short');
  const [tone, setTone] = useState<'professional' | 'casual' | 'humorous' | 'educational'>('professional');
  const [generating, setGenerating] = useState(false);
  
  // Post Conversion State
  const [postContent, setPostContent] = useState('');
  const [postDuration, setPostDuration] = useState(60);
  const [convertingPost, setConvertingPost] = useState(false);
  
  // Results State
  const [scriptResult, setScriptResult] = useState<VideoScriptResponse | null>(null);
  const [postScriptResult, setPostScriptResult] = useState<VideoFromPostResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generateScript = useCallback(async () => {
    if (!topic.trim()) return;

    setGenerating(true);
    setError(null);

    try {
      const response = await videoAPI.generateVideoScript({
        topic: topic.trim(),
        platform: platform as any,
        duration,
        tone
      });

      if (response.success) {
        setScriptResult(response);
        onScriptGenerated?.(response);
      } else {
        throw new Error(response.error || 'Script generation failed');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to generate video script');
      console.error('Video script generation error:', err);
    } finally {
      setGenerating(false);
    }
  }, [topic, platform, duration, tone, onScriptGenerated]);

  const convertPostToScript = useCallback(async () => {
    if (!postContent.trim()) return;

    setConvertingPost(true);
    setError(null);

    try {
      const response = await videoAPI.generateVideoFromPost({
        post_content: postContent.trim(),
        duration: postDuration
      });

      if (response.success) {
        setPostScriptResult(response);
      } else {
        throw new Error(response.error || 'Post conversion failed');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to convert post to video script');
      console.error('Post conversion error:', err);
    } finally {
      setConvertingPost(false);
    }
  }, [postContent, postDuration]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <SmartDisplay sx={{ mr: 1 }} />
          Enhanced Video Scripts Generator
        </Typography>
        
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Generate from Topic" icon={<AutoAwesome />} />
          <Tab label="Convert Post to Script" icon={<Edit />} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tab 1: Generate from Topic */}
        {tabValue === 0 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Video Topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., How to improve LinkedIn engagement"
                  variant="outlined"
                  disabled={generating}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    disabled={generating}
                  >
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                    <MenuItem value="youtube">YouTube</MenuItem>
                    <MenuItem value="tiktok">TikTok</MenuItem>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="x">X (Twitter)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value as any)}
                    disabled={generating}
                  >
                    <MenuItem value="short">Short (60s)</MenuItem>
                    <MenuItem value="medium">Medium (2m)</MenuItem>
                    <MenuItem value="long">Long (3m)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Tone</InputLabel>
                  <Select
                    value={tone}
                    onChange={(e) => setTone(e.target.value as any)}
                    disabled={generating}
                  >
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                    <MenuItem value="humorous">Humorous</MenuItem>
                    <MenuItem value="educational">Educational</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  onClick={generateScript}
                  disabled={!topic.trim() || generating}
                  startIcon={generating ? <CircularProgress size={20} /> : <PlayArrow />}
                  fullWidth
                  size="large"
                >
                  {generating ? 'Generating...' : 'Generate Script'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 2: Convert Post to Script */}
        {tabValue === 1 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="LinkedIn Post Content"
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  placeholder="Paste your LinkedIn post content here..."
                  variant="outlined"
                  disabled={convertingPost}
                  multiline
                  rows={4}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target Duration (seconds)"
                  value={postDuration}
                  onChange={(e) => setPostDuration(parseInt(e.target.value) || 60)}
                  inputProps={{ min: 30, max: 300 }}
                  disabled={convertingPost}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  onClick={convertPostToScript}
                  disabled={!postContent.trim() || convertingPost}
                  startIcon={convertingPost ? <CircularProgress size={20} /> : <VideoCall />}
                  fullWidth
                  size="large"
                >
                  {convertingPost ? 'Converting...' : 'Convert to Video Script'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Script Results */}
      {scriptResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Generated Video Script
          </Typography>
          
          {/* Script Metadata */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Platform</Typography>
                <Chip label={platform.toUpperCase()} size="small" />
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Duration</Typography>
                <Typography variant="body1">{formatDuration(scriptResult.estimated_duration)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Word Count</Typography>
                <Typography variant="body1">{scriptResult.word_count}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Model Used</Typography>
                <Typography variant="body2">{scriptResult.model_used}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => copyToClipboard(scriptResult.script)}
                fullWidth
              >
                Copy Script
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => {
                  const blob = new Blob([scriptResult.script], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `video-script-${Date.now()}.txt`;
                  a.click();
                }}
                fullWidth
              >
                Download
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                fullWidth
              >
                Preview
              </Button>
            </Grid>
          </Grid>

          {/* Script Content */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Script Content</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
                lineHeight: 1.6
              }}>
                {scriptResult.script}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Visual Cues */}
          {scriptResult.visual_cues && scriptResult.visual_cues.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Visual Cues</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {scriptResult.visual_cues.map((cue, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={cue} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>
      )}

      {/* Post Script Results */}
      {postScriptResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Converted Video Script
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Original Post Length</Typography>
                <Typography variant="body1">{postScriptResult.original_post_length} chars</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Duration</Typography>
                <Typography variant="body1">{formatDuration(postScriptResult.estimated_duration)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Word Count</Typography>
                <Typography variant="body1">{postScriptResult.word_count}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">Model Used</Typography>
                <Typography variant="body2">{postScriptResult.model_used}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ 
            bgcolor: 'grey.50', 
            p: 2, 
            borderRadius: 1,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            fontSize: '0.9rem',
            lineHeight: 1.6
          }}>
            {postScriptResult.script}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default EnhancedVideoScriptsGenerator; 